import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, Code } from 'lucide-react';
import { toast } from 'sonner';

const HtmlToJsxTool = () => {
  const [input, setInput] = useState('');

  const convertToJsx = (html) => {
    if (!html) return '';
    
    let jsx = html
      // Convert class to className
      .replace(/\bclass=/g, 'className=')
      // Convert for to htmlFor
      .replace(/\bfor=/g, 'htmlFor=')
      // Convert tabindex to tabIndex
      .replace(/\btabindex=/g, 'tabIndex=')
      // Convert readonly to readOnly
      .replace(/\breadonly\b/g, 'readOnly')
      // Convert autocomplete to autoComplete
      .replace(/\bautocomplete=/g, 'autoComplete=')
      // Convert autofocus to autoFocus
      .replace(/\bautofocus\b/g, 'autoFocus')
      // Convert maxlength to maxLength
      .replace(/\bmaxlength=/g, 'maxLength=')
      // Convert minlength to minLength
      .replace(/\bminlength=/g, 'minLength=')
      // Convert colspan to colSpan
      .replace(/\bcolspan=/g, 'colSpan=')
      // Convert rowspan to rowSpan
      .replace(/\browspan=/g, 'rowSpan=')
      // Convert cellpadding to cellPadding
      .replace(/\bcellpadding=/g, 'cellPadding=')
      // Convert cellspacing to cellSpacing
      .replace(/\bcellspacing=/g, 'cellSpacing=')
      // Convert onclick to onClick etc
      .replace(/\bon([a-z]+)=/gi, (match, event) => `on${event.charAt(0).toUpperCase()}${event.slice(1)}=`)
      // Self-close void elements
      .replace(/<(img|input|br|hr|meta|link|area|base|col|embed|param|source|track|wbr)([^>]*?)(?<!\/)>/gi, '<$1$2 />')
      // Convert style strings to objects (basic)
      .replace(/style="([^"]+)"/g, (match, styles) => {
        const styleObj = styles.split(';')
          .filter(s => s.trim())
          .map(s => {
            const [prop, val] = s.split(':').map(x => x.trim());
            const camelProp = prop.replace(/-([a-z])/g, (m, c) => c.toUpperCase());
            return `${camelProp}: "${val}"`;
          })
          .join(', ');
        return `style={{ ${styleObj} }}`;
      })
      // Comments
      .replace(/<!--([\s\S]*?)-->/g, '{/* $1 */}');

    return jsx;
  };

  const output = convertToJsx(input);

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success('JSX copied!');
  };

  return (
    <div className="space-y-6" data-testid="html-jsx-tool">
      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Code className="w-4 h-4 text-primary" />
          Conversions Applied
        </h3>
        <ul className="text-sm text-muted-foreground grid grid-cols-2 md:grid-cols-3 gap-1">
          <li>• class → className</li>
          <li>• for → htmlFor</li>
          <li>• onclick → onClick</li>
          <li>• style string → object</li>
          <li>• Self-closing tags</li>
          <li>• HTML comments → JSX</li>
        </ul>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">HTML Input</label>
          <Textarea
            placeholder='<div class="container" style="color: red;">...</div>'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="min-h-[300px] font-mono text-sm bg-muted/50"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">JSX Output</label>
          <Textarea
            value={output}
            readOnly
            className="min-h-[300px] font-mono text-sm bg-muted/50"
            placeholder="JSX output will appear here..."
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyOutput} disabled={!output}>
          <Copy className="w-4 h-4 mr-2" /> Copy JSX
        </Button>
        <Button onClick={() => setInput('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default HtmlToJsxTool;
